import React, { useMemo, useState } from "react";
import { Search, ArrowRight } from "lucide-react";
import { useClient } from "@/lib/clientContext";

export const CONN_DOT = { connected: "#22C55E", roster: "#F4B740", notconnected: "#EF4444" };
export const CONN_LABEL = { connected: "Connected to EHI", roster: "Roster / migrated data", notconnected: "Not connected" };

function ClientRow({ c, state, onPick, idx }) {
  return (
    <button
      data-testid={`client-picker-option-${c.client_id}`}
      onClick={() => onPick(c.client_id)}
      className="cpo-row group w-full flex items-center gap-3 px-4 py-3 rounded-[11px] text-left border border-[#ECECEE] bg-white hover:border-[#141412] hover:bg-[#FAFAF9] transition-colors"
      style={{ animationDelay: `${60 + idx * 35}ms` }}
    >
      <span className="w-[9px] h-[9px] rounded-full flex-shrink-0" style={{ background: CONN_DOT[state] }} title={CONN_LABEL[state]} />
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-bold text-[#141412] tabular-nums">Client {c.client_id}</span>
        <span className="block text-[12px] text-[#6E6E6B] truncate">{c.description || "No description"} · {CONN_LABEL[state]}</span>
      </span>
      <ArrowRight size={15} className="text-[#B5B5B1] group-hover:text-[#141412] group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
    </button>
  );
}

export default function ClientPickerOverlay() {
  const { clients, needsChoice, setSelectedClientId, connectionStateOf } = useClient();
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients;
    return clients.filter((c) => `${c.client_id} ${c.description || ""}`.toLowerCase().includes(s));
  }, [clients, q]);
  if (!needsChoice) return null;
  return (
    <div data-testid="client-picker-overlay" className="fixed inset-0 z-[1000] flex items-center justify-center p-4" style={{ background: "rgba(20,20,18,.42)", backdropFilter: "blur(14px)" }}>
      <style>{`@keyframes cpoIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}.cpo-card{animation:cpoIn .28s ease-out both}.cpo-row{animation:cpoIn .3s ease-out both}`}</style>
      <div className="cpo-card w-full max-w-[460px] bg-white rounded-[18px] p-6 sm:p-7" style={{ boxShadow: "0 30px 80px rgba(20,20,18,.28)" }} role="dialog" aria-modal="true" aria-labelledby="cpo-title">
        <img src="/revq-logo-trim.png" alt="revQ" className="h-[18px] w-auto mb-5" />
        <h1 id="cpo-title" data-testid="client-picker-title" className="text-[22px] sm:text-[24px] font-bold tracking-[-.02em] text-[#141412]">Choose the client you're working in</h1>
        <p className="text-[13px] text-[#6E6E6B] mt-1.5 mb-5">This account has {clients.length} clients. Everything you see this session — claims, patients, documents, payments — is scoped to the one you pick. You can switch later from the sidebar.</p>
        {clients.length > 6 ? (
          <label className="flex items-center gap-2 px-3 h-10 mb-3 rounded-[10px] border border-[#E4E4E7] focus-within:border-[#141412]">
            <Search size={14} className="text-[#9B9B97]" />
            <input data-testid="client-picker-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client ID or name" className="flex-1 bg-transparent outline-none text-[13px]" />
          </label>
        ) : null}
        <div className="flex flex-col gap-2 max-h-[46vh] overflow-y-auto pr-0.5" data-testid="client-picker-list">
          {list.map((c, i) => <ClientRow key={c.client_id} c={c} idx={i} state={connectionStateOf(c.client_id)} onPick={setSelectedClientId} />)}
          {list.length === 0 ? <div className="text-[12.5px] text-[#9B9B97] py-4 text-center" data-testid="client-picker-empty">No client matches "{q}"</div> : null}
        </div>
      </div>
    </div>
  );
}
